import { useEffect, useState } from "react";
import { TeamMembersInfoDisplay } from "./team-members-info-display";
import { TeamMemberEdit } from "./team-member-edit";
import { TeamMemberAdd } from "./team-member-add";
import { useTeam } from "../../../context/TeamContext";
import { TMConfirmRemovalModal } from "./tm-confirm-removal-modal";
import { useNavigate, useLocation } from "react-router-dom";
import { TeamFamilyStatus } from "./team-family-status";
import { NewCredentialsDisplay } from "../../../components/new-credentials-display";
import { TeamWithDetails } from "../../../types/team.types";

interface ITMInfoPrimaryUserProps {
  team: TeamWithDetails;
}

export function TMInfoPrimaryUser({ team }: ITMInfoPrimaryUserProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<number | null>(null);
  const [deletingMember, setDeletingMember] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { newMemberCredentials, setNewMemberCredentials, removeMember } =
    useTeam();
  const navigate = useNavigate();
  const location = useLocation();

  const handleCancel = () => {
    navigate("#");
  };

  useEffect(() => {
    const handleHashChange = () => {
      setIsCreating(location.hash === "#create");
      const hashParts = location.hash.split("=");
      if (hashParts[0] === "#edit" && hashParts[1]) {
        setEditingMemberId(parseInt(hashParts[1], 10));
      } else {
        setEditingMemberId(null);
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [location.hash]);

  const handleCreate = () => {
    navigate("#create");
  };

  const handleEdit = (memberId: number) => {
    navigate(`#edit=${memberId}`);
  };

  const handleDelete = (memberId: number, memberName: string) => {
    setDeletingMember({ name: memberName, id: memberId });
    setIsConfirmingDelete(true);
  };

  const handleNewMemberSuccess = (credentials: {
    firstName: string;
    lastName: string;
    username: string;
    password: string;
  }) => {
    setNewMemberCredentials(credentials);
    navigate("#");
  };

  const handleRemoveMember = async () => {
    if (deletingMember) {
      await removeMember(deletingMember.id);
      setIsConfirmingDelete(false);
    }
  };

  const handleBackCredentialsDisplay = () => {
    setNewMemberCredentials(null);
  };

  return (
    <div>
      {isConfirmingDelete && (
        <TMConfirmRemovalModal
          isOpen={isConfirmingDelete}
          onClose={() => setIsConfirmingDelete(false)}
          onConfirm={handleRemoveMember}
          memberName={deletingMember!.name}
        />
      )}
      {newMemberCredentials ? (
        <NewCredentialsDisplay
          firstName={newMemberCredentials.firstName}
          lastName={newMemberCredentials.lastName}
          username={newMemberCredentials.username}
          password={newMemberCredentials.password}
          onBack={handleBackCredentialsDisplay}
          className="mt-4"
        />
      ) : editingMemberId !== null ? (
        <TeamMemberEdit
          onCancel={handleCancel}
          userId={editingMemberId}
        />
      ) : isCreating ? (
        <TeamMemberAdd
          onCancel={handleCancel}
          onSuccess={handleNewMemberSuccess}
        />
      ) : (
        <div>
          <TeamFamilyStatus team={team} />
          <TeamMembersInfoDisplay
            onCreate={handleCreate}
            onEdit={handleEdit}
            onDelete={handleDelete}
            team={team!}
          />
        </div>
      )}
    </div>
  );
}
